import React, { useState, useRef, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Send, Paperclip, Smile, MoreVertical, Facebook, Instagram, MessageCircle, Search } from 'lucide-react';
import { mockMessages } from '../data/mockData';
import { Badge } from '../components/ui/badge';

const ChatInterface = () => {
  const [selectedChat, setSelectedChat] = useState(mockMessages[0]);
  const [messageText, setMessageText] = useState('');
  const [messages, setMessages] = useState(selectedChat.messages);
  const messagesEndRef = useRef(null);

  const platformIcons = {
    messenger: Facebook,
    instagram: Instagram,
    whatsapp: MessageCircle
  };

  useEffect(() => {
    setMessages(selectedChat.messages);
    scrollToBottom();
  }, [selectedChat]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    const newMessage = {
      id: `m${Date.now()}`,
      sender: 'bot',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages([...messages, newMessage]);
    setMessageText('');
    setTimeout(scrollToBottom, 100);
  };

  return (
    <DashboardLayout>
      <div className="flex h-full">
        {/* Chat List Sidebar */}
        <div className="w-80 bg-white border-r flex flex-col">
          <div className="p-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search conversations..." className="pl-9" />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {mockMessages.map((chat) => {
              const PlatformIcon = platformIcons[chat.platform];
              const lastMsg = chat.messages[chat.messages.length - 1];
              const isSelected = selectedChat.id === chat.id;
              
              return (
                <div
                  key={chat.id}
                  onClick={() => setSelectedChat(chat)}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                    isSelected ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Avatar>
                      <AvatarFallback>{chat.contactName.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-gray-900 truncate">{chat.contactName}</p>
                        {chat.unread > 0 && (
                          <Badge className="bg-blue-600 text-white text-xs">{chat.unread}</Badge>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mb-1">
                        {PlatformIcon && <PlatformIcon className="h-3 w-3 text-gray-400" />}
                        <span className="text-xs text-gray-500 capitalize">{chat.platform}</span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">{lastMsg.content}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(lastMsg.timestamp).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Chat Header */}
          <div className="bg-white border-b p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Avatar>
                  <AvatarFallback>{selectedChat.contactName.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-gray-900">{selectedChat.contactName}</p>
                  <div className="flex items-center space-x-2">
                    {platformIcons[selectedChat.platform] && 
                      React.createElement(platformIcons[selectedChat.platform], { 
                        className: 'h-3 w-3 text-gray-400' 
                      })
                    }
                    <span className="text-xs text-gray-500 capitalize">{selectedChat.platform}</span>
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            <div className="space-y-4">
              {messages.map((message) => {
                const isBot = message.sender === 'bot';
                return (
                  <div
                    key={message.id}
                    className={`flex ${isBot ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        isBot
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-900 border'
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                      <p
                        className={`text-xs mt-1 ${
                          isBot ? 'text-blue-100' : 'text-gray-400'
                        }`}
                      >
                        {new Date(message.timestamp).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Message Input */}
          <div className="bg-white border-t p-4">
            <form onSubmit={handleSendMessage}>
              <div className="flex items-center space-x-2">
                <Button type="button" variant="ghost" size="icon">
                  <Paperclip className="h-5 w-5" />
                </Button>
                <Button type="button" variant="ghost" size="icon">
                  <Smile className="h-5 w-5" />
                </Button>
                <Input
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1"
                />
                <Button type="submit" size="icon">
                  <Send className="h-5 w-5" />
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ChatInterface;
